import { IOracleFactory } from '@arcxgame/contracts/dist/src/typings/IOracleFactory'
import { eqIgnoreCase } from '@arcxmoney/arcx-core'
import { poll } from '@arcxmoney/arcx-react'
import { BigNumber, constants, Signer, utils } from 'ethers'
import { useEffect, useState } from 'react'
import { Synth } from 'src/services/GraphService'
import { diff } from 'src/utils/diff'

export interface OraclePrice {
  price: BigNumber
  asset: string
}

export const useOraclePrices = (signer?: Signer, synths?: Synth[]) => {
  const [prices, setPrices] = useState<OraclePrice[]>([])
  const getPrice = (assetAddress: string) =>
    prices.find(({ asset }) => eqIgnoreCase(asset, assetAddress))?.price || constants.Zero

  useEffect(() => {
    if (!signer) {
      return
    }

    const oracles = synths.map(({ oracle }) => IOracleFactory.connect(oracle, signer))

    return poll(async () => {
      const newPrices: OraclePrice[] = []
      for (const oracle of oracles) {
        try {
          newPrices.push({
            asset: oracle.address,
            price: (await oracle.fetchCurrentPrice()).value,
          })
        } catch (err) {
          console.log('Cannot fetch price for oracle:', oracle.address)
          console.error(err)
        }
      }
      newPrices.sort()
      setPrices((prevPrices) => {
        if (arePricesChanged(newPrices, prevPrices, 2)) {
          return newPrices
        } else {
          return prevPrices
        }
      })
    }, 5000)
  }, [signer, synths])
  return {
    prices,
    getPrice,
  }
}

export function arePricesChanged(
  newPrices: OraclePrice[],
  prevPrices: OraclePrice[],
  thresholdPercent = 0,
) {
  if (newPrices.length !== prevPrices.length) {
    return true
  }
  for (const newPrice of newPrices) {
    const prevPrice = prevPrices.find(({ asset }) => asset === newPrice.asset)
    if (!prevPrice) {
      return true
    }
    const threshold = prevPrice.price
      .mul(utils.parseEther(thresholdPercent.toString()))
      .div(constants.WeiPerEther.mul(100))
    if (diff(newPrice.price, prevPrice.price).gt(threshold)) {
      return true
    }
  }
  return false
}
